<script lang="ts" setup>
//@ts-ignore
import Paginate from "vuejs-paginate-next";
import { computed, ref, watch } from 'vue'

const emit = defineEmits(['goToPage']);

interface Props {
    totalItems: number
    currentPage: number
    pageSize: number
    maxResultWindow?: number
}
const props = withDefaults(defineProps<Props>(), {
    totalItems: 0,
    currentPage: 1,
    pageSize: 10,
    maxResultWindow: Number.POSITIVE_INFINITY,
})

// Create a local ref synced with the prop
const currentPage = ref(props.currentPage)


const range = (start: number, end: number) => {
    return Array.from(
        Array(Math.abs(end - start) + 1),
        (_, i) => start + i
    );
}

const totalPages = computed(() => {
    if (props.pageSize <= 0) return 0;
    if (props.totalItems <= 0) return 0;
    return Math.ceil(props.totalItems / props.pageSize);
});

const maxWindowPages = computed(() => {
    if (!Number.isFinite(props.maxResultWindow) || props.maxResultWindow <= 0) {
        return Number.POSITIVE_INFINITY;
    }
    if (props.pageSize <= 0) return Number.POSITIVE_INFINITY;
    return Math.floor(props.maxResultWindow / props.pageSize);
});

const limitedPageCount = computed(() => {
    const total = totalPages.value;
    if (total === 0) return 0;
    const windowPages = maxWindowPages.value;
    if (!Number.isFinite(windowPages)) return total;
    const limited = Math.min(total, windowPages);
    return limited > 0 ? limited : 1;
});

const clampPageToAvailableRange = (pageNo: number) => {
    if (limitedPageCount.value <= 0) return pageNo;
    if (!pageNo || pageNo < 1) return 1;
    return Math.min(pageNo, limitedPageCount.value);
}

currentPage.value = clampPageToAvailableRange(props.currentPage);

const pageNumbers = computed(() => {
    if (props.pageSize <= 0) return [];
    if( props.totalItems <= 0) return [];
    const totalPageNos = limitedPageCount.value;
    if (totalPageNos <= 0) return [];
    return range(1, totalPageNos);
});

//emits
const handlePageChange = (pageNo: number) => {
    const targetPage = clampPageToAvailableRange(pageNo);
    if (props.currentPage !== targetPage) {
        emit('goToPage', targetPage);
    }
};

// Watch for prop changes → update local
watch(() => props.currentPage, (newVal) => {
  currentPage.value = clampPageToAvailableRange(newVal)
})


</script>
<template>
     <paginate
        v-model="currentPage"
        :page-count="pageNumbers.length"
        :click-handler="handlePageChange"
        :prev-text="'Prev'"
        :next-text="'Next'"
        container-class="sai-pagination"
        prev-class="prev"
        next-class="next"
        page-class="page-item"
        active-class="active"
        break-view-class="break-view"
        disabled-class="disabled"
        :page-range="2"
        :margin-pages="2"
      >
      </paginate>
</template>
<style lang="scss">
    @use './style.scss';
</style>
